import { useEffect, useState, useRef, useCallback, useMemo, memo, type FC } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import type { SigningField } from '../types/fields';
import { PdfPage } from './PdfPage';
import { UI_PREFS_STORAGE_KEY } from '../constants/signing';
import { clampViewerScale, computeFitToWidthScale, computeFitToHeightScale, nextZoomStep } from '../utils/viewerScale';
import type { PdfDocumentState } from '../hooks/usePdfDocument';
import { getDocumentPrefsKey } from '../utils/documentPrefs';
import { parsePdfUserError } from '../utils/pdfUserError';

// Shared empty diff-status map for the no-compare case. Hoisted to module scope so
// PdfPage's prop reference is stable across renders → React.memo bailout works.
const EMPTY_STATUS_MAP: ReadonlyMap<string, { status: 'added' | 'removed' | 'changed' | 'unchanged' }> = new Map();

// Shared empty fields array for pages with no fields. Hoisted to module scope so the
// reference is stable across renders → field-less PdfPage instances bail out of React.memo
// instead of receiving a fresh `[]` on every viewer render.
const EMPTY_FIELDS: SigningField[] = [];

// Worker is configured once in main.tsx (see comments there for Vite dev/prod details).
// We still import pdfjsLib here for PDFDocumentProxy type + getDocument().

interface PdfViewerProps {
  file: File | null;
  /** Currently unused (upload success is tracked via parent state) */
  fileId?: string | null;
  fields: SigningField[];
  selectedFieldId: string | null;
  currentTool: SigningField['type'] | 'select' | 'image' | null;
  onPlaceField: (pageIndex: number, pdfX: number, pdfY: number, renderScale: number) => void;
  onFieldUpdate: (id: string, updates: Partial<SigningField>) => void;
  onFieldSelect: (id: string | null) => void;
  onFieldDelete: (id: string) => void;
  scale?: number;
  /** Fired whenever the effective viewer scale changes (zoom controls, fit-to-width, prefs restore). */
  onScaleChange?: (scale: number) => void;
  /** Called with pdf.js viewport sizes once measurement for the current file is complete. */
  onLivePageSizes?: (
    sizes: Array<{ width: number; height: number; rotation?: number }>,
    epoch: number
  ) => void;
  /** Called by active PdfPage instances with a small thumbnail of their current render */
  onThumbnailUpdate?: (pageIndex: number, dataUrl: string) => void;
  /** Receives the current map of live thumbnails (for PageNavigator) */
  onLiveThumbnailsChange?: (thumbnails: Record<number, string>) => void;
  /** Optional: fields from a snapshot to render as "ghost" for visual comparison */
  compareFields?: any[] | null;
  /** Human-friendly name for the active snapshot (used by the compare banner) */
  compareLabel?: string | null;
  /** Callback to exit compare mode from viewer banner */
  onExitCompare?: () => void;
  /** Global pan mode from parent (makes the hand tool global across the app) */
  panMode?: boolean;
  onTogglePan?: () => void;
  /** Inline text editor on the PDF canvas */
  inlineEditingFieldId?: string | null;
  onStartInlineEdit?: (fieldId: string) => void;
  onCommitInlineEdit?: (fieldId: string, value: string, editorBox: { width: number; height: number; displayScale: number }) => void;
  onCancelInlineEdit?: () => void;
  inlineEditDismissRef?: React.MutableRefObject<boolean>;
  /** Fired when the page most visible in the document scroll area changes */
  onVisiblePageChange?: (pageIndex: number) => void;
  /** Place a copy of the field at the same position on every page (signature/initials/date) */
  onDuplicateToAllPages?: (fieldId: string) => void;
  /** When set, skips internal pdfjs load and reuses this shared document (from usePdfDocument). */
  sharedPdf?: PdfDocumentState;
  /** Stable document name for prefs key (matches SigningWorkspace originalFileName). */
  documentName?: string | null;
  /** Page index to always render (virtualization priority). */
  activePageIndex?: number;
  /**
   * Called when the user finishes a freehand ink stroke while the 'ink' tool is active.
   * points are already in PDF coordinate space (bottom-left origin).
   * The wiring agent implements this to call addField for an 'ink' type field.
   */
  onPlaceInk?: (pageIndex: number, points: Array<{ x: number; y: number }>, renderScale: number) => void;
}

/** Field types that make sense to stamp onto every page of a document. */
const COPY_ALL_PAGES_TYPES: ReadonlyArray<SigningField['type']> = [
  'signature-draw',
  'signature-upload',
  'signature-type',
  'initials',
  'date',
];

/**
 * Format today's date for the quick-fill button, matching the field's existing
 * format when it is recognizable (ISO / dotted-European), else the browser locale
 * default used at placement time.
 */
function formatTodayLike(existing?: string): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const trimmed = (existing ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }
  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(trimmed)) {
    return `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()}`;
  }
  try {
    return now.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return now.toLocaleDateString();
  }
}

const PdfViewerComponent: FC<PdfViewerProps> = ({
  file,
  fileId: _fileId,
  fields,
  selectedFieldId,
  currentTool,
  onPlaceField,
  onFieldUpdate,
  onFieldSelect,
  onFieldDelete,
  scale: externalScale,
  onScaleChange,
  // Ensure all control + compare props are properly received (previous incomplete destructure
  // silently broke compareFields, panMode from parent, live size callbacks, etc.)
  onLivePageSizes,
  onLiveThumbnailsChange,
  compareFields,
  panMode,
  onTogglePan,
  compareLabel,
  onExitCompare,
  inlineEditingFieldId = null,
  onStartInlineEdit,
  onCommitInlineEdit,
  onCancelInlineEdit,
  inlineEditDismissRef,
  onVisiblePageChange,
  onDuplicateToAllPages,
  sharedPdf,
  documentName,
  activePageIndex = 0,
  onPlaceInk,
}) => {
  // Internal zoom control (can be driven externally later)
  const [internalScale, setInternalScale] = useState(1);
  const scale = externalScale ?? internalScale;

  useEffect(() => {
    onScaleChange?.(scale);
  }, [scale, onScaleChange]);

  const pageColumnRef = useRef<HTMLDivElement>(null);
  const fitAppliedForDocRef = useRef<string | null>(null);
  // Live thumbnails from actually rendered pages (high-impact optimization).
  // Stored in a ref (not state) — parent callback owns the authoritative copy.
  const liveThumbnailsRef = useRef<Record<number, string>>({});

  // High-impact "Show Original" / Compare mode
  // When false, placed fields are hidden so user can see the clean original PDF
  const [showPlacedFields, setShowPlacedFields] = useState(true);

  // === PERSISTENCE (per-document via file name / uploadedFileId) ===
  // Restores zoom + showPlacedFields on PDF load (and survives snapshot loads since doc unchanged).
  useEffect(() => {
    if (!file) {
      fitAppliedForDocRef.current = null;
      return;
    }
    fitAppliedForDocRef.current = null;
    const docKey = getDocumentPrefsKey(_fileId, documentName ?? file.name);
    const storageKey = `${UI_PREFS_STORAGE_KEY}:${docKey}`;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const prefs = JSON.parse(raw) as { zoom?: number; showPlacedFields?: boolean };
        if (typeof prefs.zoom === 'number') {
          setInternalScale(clampViewerScale(prefs.zoom));
          fitAppliedForDocRef.current = docKey;
        }
        if (typeof prefs.showPlacedFields === 'boolean') {
          setShowPlacedFields(prefs.showPlacedFields);
        }
      }
    } catch {
      // ignore corrupt prefs
    }
  }, [file, _fileId, documentName]);

  // Persist zoom + showPlaced whenever they change for current doc
  useEffect(() => {
    if (!file) return;
    const docKey = getDocumentPrefsKey(_fileId, documentName ?? file.name);
    const storageKey = `${UI_PREFS_STORAGE_KEY}:${docKey}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        zoom: internalScale,
        showPlacedFields,
      }));
    } catch {
      // quota or disabled storage – non-fatal
    }
  }, [file, _fileId, documentName, internalScale, showPlacedFields]);

  // Pan hand tool - supports both controlled (global from parent) and uncontrolled mode
  const [localPanMode, setLocalPanMode] = useState(false);
  const effectivePanMode = panMode !== undefined ? !!panMode : localPanMode;
  const setEffectivePan = onTogglePan || setLocalPanMode;
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // Opacity for snapshot ghost layer (user controllable 0.2-0.8 for better visibility trade-off)
  const [compareOpacity, setCompareOpacity] = useState(0.45);

  // === LIGHTWEIGHT DIFF SUMMARY (high-impact, zero-deps) ===
  // Computed here once for the compare banner AND shared with PdfPage as a Map<id,status>
  // so each page no longer needs to filter+find for every ghost field on every render.
  // Previous shape did O(P×F) per render of the viewer; now O(N+M) once.
  // +added (current only), -removed (snapshot only), changed (ID match but moved/value diff), same (exact).
  // Compare-mode diff is only meaningful while a snapshot is loaded. Because `fields`
  // gets a brand-new array reference on every pointermove during a drag/resize
  // (useSigningFields.commit → setFields(prev.slice())), driving the O(N+M) diff
  // directly off `fields` would recompute it — and churn a new ghostStatusById Map
  // identity that re-renders every page's ghost layer — 60-120x/sec.
  //
  // Coalesce the diff's view of `fields` to at most once per animation frame, and
  // ONLY while compare mode is active. When compare mode is off we mirror `fields`
  // synchronously so nothing downstream is delayed (the memo short-circuits to
  // EMPTY_STATUS_MAP there anyway and the coalescing cost is irrelevant).
  const compareActive = !!compareFields && compareFields.length > 0;
  const [diffFields, setDiffFields] = useState<SigningField[]>(fields);
  const diffRafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!compareActive) {
      // No snapshot → keep diffFields in lockstep with fields (cheap, memo is a no-op).
      if (diffRafRef.current !== null) {
        cancelAnimationFrame(diffRafRef.current);
        diffRafRef.current = null;
      }
      setDiffFields(fields);
      return;
    }
    // Snapshot active → coalesce: schedule a single update on the next frame, so a
    // burst of pointermoves collapses into one diff recompute / one Map identity.
    if (diffRafRef.current !== null) return; // a frame is already pending
    diffRafRef.current = requestAnimationFrame(() => {
      diffRafRef.current = null;
      setDiffFields(fields);
    });
    return () => {
      if (diffRafRef.current !== null) {
        cancelAnimationFrame(diffRafRef.current);
        diffRafRef.current = null;
      }
    };
  }, [fields, compareActive]);

  const { diffSummary, ghostStatusById } = useMemo(() => {
    if (!compareFields || compareFields.length === 0) {
      return { diffSummary: null, ghostStatusById: EMPTY_STATUS_MAP };
    }
    const snap = compareFields;
    const curr = diffFields;

    // Index current fields by id once so the inner loop is O(1) lookup instead of O(N) .find().
    const currById = new Map<string, SigningField>();
    for (let i = 0; i < curr.length; i++) {
      const f = curr[i];
      currById.set(f.id, f);
    }
    // Index snapshot ids once for the "added" pass.
    const snapIds = new Set<string>();
    for (let i = 0; i < snap.length; i++) snapIds.add(snap[i].id);

    let added = 0, removed = 0, changed = 0, unchanged = 0;
    const status = new Map<string, { status: 'added' | 'removed' | 'changed' | 'unchanged' }>();

    for (let i = 0; i < snap.length; i++) {
      const ghost = snap[i];
      const match = currById.get(ghost.id);
      if (!match) {
        removed++;
        status.set(ghost.id, { status: 'removed' });
        continue;
      }
      const tol = 0.6;
      const posChanged =
        Math.abs((ghost.x ?? 0) - match.x) > tol ||
        Math.abs((ghost.y ?? 0) - match.y) > tol ||
        Math.abs((ghost.width ?? 0) - match.width) > tol ||
        Math.abs((ghost.height ?? 0) - match.height) > tol;
      const valChanged =
        (ghost.value || '') !== (match.value || '') ||
        (ghost.isChecked ?? false) !== (match.isChecked ?? false);
      if (posChanged || valChanged) {
        changed++;
        status.set(ghost.id, { status: 'changed' });
      } else {
        unchanged++;
        status.set(ghost.id, { status: 'unchanged' });
      }
    }

    // "Added" = current fields with no matching ghost id.
    for (let i = 0; i < curr.length; i++) {
      if (!snapIds.has(curr[i].id)) added++;
    }

    const parts: string[] = [];
    if (added > 0) parts.push(`+${added} added`);
    if (removed > 0) parts.push(`-${removed} removed`);
    if (changed > 0) parts.push(`${changed} changed`);
    if (unchanged > 0) parts.push(`${unchanged} same`);
    return {
      diffSummary: parts.length ? parts.join(' • ') : 'Identical to snapshot',
      ghostStatusById: status,
    };
  }, [compareFields, diffFields]);

  // Compute early so it is available to all effects (prevents TDZ surprises in closures)
  const isPlacing = currentTool !== null && currentTool !== 'select';

  // Callback for PdfPage to register its current rendered canvas as a thumbnail
  const handleThumbnailUpdate = useCallback((pageIndex: number, dataUrl: string) => {
    if (liveThumbnailsRef.current[pageIndex] === dataUrl) return;
    const next = { ...liveThumbnailsRef.current, [pageIndex]: dataUrl };
    liveThumbnailsRef.current = next;
    onLiveThumbnailsChange?.(next);
  }, [onLiveThumbnailsChange]);

  // Track last mouse position relative to the container for true zoom-to-cursor
  const lastMouseRef = useRef({ x: 0.5, y: 0.5 });

  const zoomBy = useCallback((delta: number) => {
    const container = document.querySelector('.editor-stage-viewer') as HTMLElement;
    if (!container) {
      setInternalScale(s => Math.max(0.5, Math.min(4, Math.round((s + delta) * 100) / 100)));
      return;
    }

    const rect = container.getBoundingClientRect();
    const mouseX = lastMouseRef.current.x * rect.width;
    const mouseY = lastMouseRef.current.y * rect.height;

    const oldScale = scale;
    const newScale = Math.max(0.5, Math.min(4, Math.round((oldScale + delta) * 100) / 100));

    // Calculate scroll adjustment to keep the point under cursor stable
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;

    const contentX = scrollLeft + mouseX;
    const contentY = scrollTop + mouseY;

    const newScrollLeft = (contentX / oldScale) * newScale - mouseX;
    const newScrollTop = (contentY / oldScale) * newScale - mouseY;

    setInternalScale(newScale);

    requestAnimationFrame(() => {
      container.scrollLeft = Math.max(0, newScrollLeft);
      container.scrollTop = Math.max(0, newScrollTop);
    });
  }, [scale]);

  // Track mouse position for better zoom-to-cursor + wheel support
  useEffect(() => {
    const container = document.querySelector('.editor-stage-viewer');
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      lastMouseRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      };
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.2 : 0.2;
        zoomBy(delta);
      }
    };

    container.addEventListener('mousemove', handleMouseMove as EventListener);
    container.addEventListener('wheel', handleWheel as EventListener, { passive: false });

    return () => {
      container.removeEventListener('mousemove', handleMouseMove as EventListener);
      container.removeEventListener('wheel', handleWheel as EventListener);
    };
  }, [zoomBy]);

  // Ctrl/Cmd keyboard zoom (+, -, 0) — fire only when focus is NOT inside a text input/editor
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      // Guard: do not intercept when user is typing in an input, textarea, or contenteditable
      const isTyping =
        tag === 'input' || tag === 'textarea' || (e.target as HTMLElement)?.isContentEditable;
      if (isTyping) return;

      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        setInternalScale((s) => nextZoomStep(s, 1));
      } else if (e.key === '-') {
        e.preventDefault();
        setInternalScale((s) => nextZoomStep(s, -1));
      } else if (e.key === '0') {
        e.preventDefault();
        setInternalScale(1.0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Pan hand tool handlers (high-impact precise navigation)
  useEffect(() => {
    const container = document.querySelector('.editor-stage-viewer') as HTMLElement;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (!effectivePanMode) return;
      // Don't pan if clicking on interactive field elements
      if ((e.target as HTMLElement).closest('.signing-field')) return;

      isDraggingRef.current = true;
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
      };
      container.style.cursor = 'grabbing';
    };

    const handleMouseMovePan = (e: MouseEvent) => {
      if (!isDraggingRef.current || !effectivePanMode) return;

      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;

      container.scrollLeft = dragStartRef.current.scrollLeft - dx;
      container.scrollTop = dragStartRef.current.scrollTop - dy;
    };

    const handleMouseUp = () => {
      if (!effectivePanMode) return;
      isDraggingRef.current = false;
      setIsDragging(false);
      container.style.cursor = 'grab';
    };

    if (effectivePanMode) {
      container.style.cursor = isDragging ? 'grabbing' : 'grab';
      container.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMovePan);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      container.style.cursor = '';
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMovePan);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [effectivePanMode, isDragging]);
  // isDragging kept in deps only for cursor styling; move handlers use isDraggingRef

  // Auto-exit pan mode when entering placement mode (better UX)
  useEffect(() => {
    if (isPlacing && effectivePanMode && onTogglePan) {
      onTogglePan(); // delegate to parent
    }
  }, [isPlacing, effectivePanMode, onTogglePan]);
  const [internalPdfDoc, setInternalPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [internalPageCount, setInternalPageCount] = useState(0);
  const [internalPageSizes, setInternalPageSizes] = useState<Array<{ width: number; height: number }>>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  const useShared = sharedPdf !== undefined;
  const pdfDoc = useShared ? sharedPdf.doc : internalPdfDoc;
  const pageCount = useShared ? sharedPdf.pageCount : internalPageCount;
  const pageSizes = useShared ? sharedPdf.pageSizes : internalPageSizes;
  const loading = useShared ? sharedPdf.loading : internalLoading;
  const error = useShared ? sharedPdf.error : internalError;

  // Drop stale page thumbnails when the underlying document changes.
  useEffect(() => {
    liveThumbnailsRef.current = {};
    onLiveThumbnailsChange?.({});
  }, [file, sharedPdf?.doc, onLiveThumbnailsChange]);

  // pdf.js viewport sizes reported by rendered PdfPage instances (authoritative visual dims).
  const measuredViewportsRef = useRef<
    Map<number, { width: number; height: number; rotation?: number }>
  >(new Map());

  useEffect(() => {
    measuredViewportsRef.current = new Map();
  }, [file, sharedPdf?.doc, pageCount]);

  const handlePageViewportMeasured = useCallback(
    (pageIndex: number, size: { width: number; height: number; rotation?: number }) => {
      const prev = measuredViewportsRef.current.get(pageIndex);
      if (
        prev &&
        prev.width === size.width &&
        prev.height === size.height &&
        (prev.rotation ?? 0) === (size.rotation ?? 0)
      ) {
        return;
      }
      measuredViewportsRef.current.set(pageIndex, size);

      if (!onLivePageSizes || pageCount <= 0) return;
      if (useShared && (sharedPdf.loading || !sharedPdf.pageSizesReady)) return;
      if (measuredViewportsRef.current.size < pageCount) return;

      const liveSizes = Array.from({ length: pageCount }, (_, i) => {
        const measured = measuredViewportsRef.current.get(i);
        if (!measured) return null;
        return measured;
      });
      if (liveSizes.every(Boolean) && sharedPdf?.sizesEpoch) {
        onLivePageSizes(
          liveSizes as Array<{ width: number; height: number; rotation?: number }>,
          sharedPdf.sizesEpoch
        );
      }
    },
    [
      onLivePageSizes,
      pageCount,
      useShared,
      sharedPdf?.sizesEpoch,
      sharedPdf?.loading,
      sharedPdf?.pageSizesReady,
    ]
  );

  // Load PDF document when file changes (skipped when sharedPdf is provided)
  useEffect(() => {
    if (useShared) return;

    if (!file) {
      setInternalPdfDoc(null);
      setInternalPageCount(0);
      setInternalPageSizes([]);
      return;
    }

    let cancelled = false;
    setInternalLoading(true);
    setInternalError(null);

    const load = async () => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const doc = await loadingTask.promise;

        if (cancelled) {
          doc.destroy();
          return;
        }

        const count = doc.numPages;
        const sizes: Array<{ width: number; height: number; rotation?: number }> = [];

        for (let i = 1; i <= count; i++) {
          const page = await doc.getPage(i);
          const viewport = page.getViewport({ scale: 1.0 });
          const rot = (page as { rotate?: number }).rotate ?? 0;
          sizes.push({ width: viewport.width, height: viewport.height, rotation: rot });
        }

        setInternalPdfDoc(doc);
        setInternalPageCount(count);
        setInternalPageSizes(sizes);
      } catch (err: unknown) {
        console.error(err);
        setInternalError(parsePdfUserError(err));
      } finally {
        if (!cancelled) setInternalLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [file, useShared]);

  const hasAccurateSizes = useShared
    ? sharedPdf.pageSizesReady && !sharedPdf.loading
    : pageSizes.length === pageCount && pageCount > 0;

  const sizesEpoch = useShared ? sharedPdf.sizesEpoch : 0;

  // Sync measured pdf.js viewport sizes once the document hook reports them ready.
  // Never pass raw server seed sizes — only after pdf.js measurement completes.
  useEffect(() => {
    if (!onLivePageSizes || !hasAccurateSizes || pageSizes.length === 0) return;
    if (useShared && (!sizesEpoch || sharedPdf.loading)) return;
    onLivePageSizes(pageSizes, sizesEpoch);
  }, [onLivePageSizes, pageSizes, hasAccurateSizes, sizesEpoch, useShared, sharedPdf?.loading]);

  const fitToWidth = useCallback(() => {
    const column = pageColumnRef.current;
    if (!column || pageSizes.length === 0) return;
    const maxPageW = Math.max(...pageSizes.map((p) => p.width));
    const fit = computeFitToWidthScale(maxPageW, column.clientWidth);
    setInternalScale(fit);
    if (file) {
      fitAppliedForDocRef.current = getDocumentPrefsKey(_fileId, documentName ?? file.name);
    }
  }, [file, _fileId, documentName, pageSizes]);

  /** Fit a whole page in the scroll viewport (constrained by height AND column width). */
  const fitToPage = useCallback(() => {
    const container = document.querySelector('.editor-stage-viewer') as HTMLElement | null;
    const column = pageColumnRef.current;
    if (!container || pageSizes.length === 0) return;
    const maxPageW = Math.max(...pageSizes.map((p) => p.width));
    const maxPageH = Math.max(...pageSizes.map((p) => p.height));
    const availableH = Math.max(200, container.clientHeight - 64);
    const availableW = Math.max(200, (column?.clientWidth ?? container.clientWidth) - 48);
    const fit = Math.min(availableH / maxPageH, availableW / maxPageW);
    setInternalScale(clampViewerScale(fit));
    if (file) {
      fitAppliedForDocRef.current = getDocumentPrefsKey(_fileId, documentName ?? file.name);
    }
  }, [file, _fileId, documentName, pageSizes]);

  /**
   * Fit the tallest page into the visible container height.
   * Exposed via the fitToHeight prop for ZoomControls / external callers.
   */
  const fitToHeight = useCallback(() => {
    const container = document.querySelector('.editor-stage-viewer') as HTMLElement | null;
    if (!container || pageSizes.length === 0) return;
    const maxPageH = Math.max(...pageSizes.map((p) => p.height));
    const fit = computeFitToHeightScale(maxPageH, container.clientHeight);
    setInternalScale(fit);
    if (file) {
      fitAppliedForDocRef.current = getDocumentPrefsKey(_fileId, documentName ?? file.name);
    }
  }, [file, _fileId, documentName, pageSizes]);

  // Selected field powering the contextual toolbar actions (All pages / Today)
  const selectedFieldObj = useMemo(
    () => (selectedFieldId ? fields.find((f) => f.id === selectedFieldId) ?? null : null),
    [fields, selectedFieldId]
  );

  // Bucket fields by page so each PdfPage receives only its own fields. Recomputed only
  // when the `fields` reference changes; field-less pages fall back to the shared
  // EMPTY_FIELDS constant. This stops a single-field edit from re-filtering the full
  // field list on every mounted page (was O(pageCount × totalFields) per edit).
  const fieldsByPage = useMemo(() => {
    const map = new Map<number, SigningField[]>();
    for (const f of fields) {
      const bucket = map.get(f.pageIndex);
      if (bucket) bucket.push(f);
      else map.set(f.pageIndex, [f]);
    }
    return map;
  }, [fields]);
  const canCopySelectedToAllPages =
    !!selectedFieldObj &&
    !!onDuplicateToAllPages &&
    COPY_ALL_PAGES_TYPES.includes(selectedFieldObj.type) &&
    pageCount > 1;
  const selectedDateField = selectedFieldObj?.type === 'date' ? selectedFieldObj : null;

  const pageIndices = useMemo(
    () => Array.from({ length: pageCount }, (_, i) => i),
    [pageCount]
  );

  // Fit page render scale when a document opens (no CSS shrink — true pixel size in the viewer).
  useEffect(() => {
    if (!file || !hasAccurateSizes || loading) return;
    const docKey = getDocumentPrefsKey(_fileId, documentName ?? file.name);
    if (fitAppliedForDocRef.current === docKey) return;

    let cancelled = false;
    let ro: ResizeObserver | null = null;

    const applyFit = () => {
      if (cancelled) return true;
      const column = pageColumnRef.current;
      if (!column || column.clientWidth <= 0) return false;
      const maxPageW = Math.max(...pageSizes.map((p) => p.width));
      setInternalScale(computeFitToWidthScale(maxPageW, column.clientWidth));
      fitAppliedForDocRef.current = docKey;
      return true;
    };

    const tryFit = () => {
      if (applyFit()) {
        ro?.disconnect();
        return;
      }
      const column = pageColumnRef.current;
      if (!column) return;
      ro ??= new ResizeObserver(() => {
        if (applyFit()) ro?.disconnect();
      });
      ro.observe(column);
    };

    const raf = requestAnimationFrame(tryFit);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [file, _fileId, documentName, hasAccurateSizes, pageSizes, loading]);

  // Track which page is visible in the document scroll area (syncs bottom filmstrip)
  // FIX-4: persist a Map<pageIndex, intersectionRatio> across IntersectionObserver
  // callbacks so the argmax runs over ALL observed pages, not just the subset that
  // changed in a given callback. Without this, mid-scroll callbacks that only include
  // a page crossing a threshold (while other fully-visible pages are not in `entries`)
  // would incorrectly report an off-screen page as the most visible one.
  // The ratio map is a ref so it survives across effect re-runs without being a dep.
  const pageRatioMapRef = useRef<Map<number, number>>(new Map());
  useEffect(() => {
    if (!pageCount || !onVisiblePageChange) return;

    const root = document.querySelector('.editor-stage-viewer');
    if (!root) return;

    // FIX-4: do NOT reset the ratio map on every reconnect — carry accumulated
    // ratios forward so partial-batch callbacks still argmax correctly.
    // (The map is keyed by pageIndex and will be overwritten with fresh values
    //  as new entries arrive, so stale entries for removed pages are harmless.)
    const ratioMap = pageRatioMapRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        // Update the persistent ratio map from this batch of changed entries.
        for (const entry of entries) {
          const match = entry.target.id.match(/^pdf-page-(\d+)$/);
          if (!match) continue;
          const idx = Number(match[1]);
          ratioMap.set(idx, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        // Argmax over ALL observed pages (not just this batch).
        let bestIndex = -1;
        let bestRatio = 0;
        ratioMap.forEach((ratio, idx) => {
          if (ratio >= bestRatio) {
            bestRatio = ratio;
            bestIndex = idx;
          }
        });
        if (bestIndex >= 0) onVisiblePageChange(bestIndex);
      },
      {
        root,
        threshold: [0, 0.15, 0.35, 0.55, 0.75, 1],
        rootMargin: '-8% 0px -40% 0px',
      }
    );

    const observePages = () => {
      for (let i = 0; i < pageCount; i += 1) {
        const el = document.getElementById(`pdf-page-${i}`);
        if (el) observer.observe(el);
      }
    };

    const raf = requestAnimationFrame(observePages);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
    // FIX-4: narrow deps — scale/loading/showPlacedFields caused the observer to
    // reconnect on every zoom, discarding the accumulated ratio map. Only pageCount
    // and onVisiblePageChange should trigger a reconnect (new pages or new handler).
  }, [pageCount, onVisiblePageChange]);

  if (!file) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[520px] text-center px-6">
        {/* Empty stage: notary's intake area. The paper silhouette
           is a visual placeholder for the document that will
           land there. The headline uses the display serif. The
           "Or" divider separates the drop zone from the
           privacy seal. */}
        <div className="editor-stage-empty" role="region" aria-label="Document intake area">
          <div className="editor-stage-empty-paper" aria-hidden="true">
            <div className="editor-stage-empty-paper-corner editor-stage-empty-paper-corner-tl" />
            <div className="editor-stage-empty-paper-corner editor-stage-empty-paper-corner-tr" />
            <div className="editor-stage-empty-paper-corner editor-stage-empty-paper-corner-bl" />
            <div className="editor-stage-empty-paper-corner editor-stage-empty-paper-corner-br" />
            <div className="editor-stage-empty-paper-line" />
            <div className="editor-stage-empty-paper-line" />
            <div className="editor-stage-empty-paper-line" />
          </div>

          <div className="mt-8 max-w-md">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-state-success">
              Local · Private
            </div>
            <h2 className="mt-2 text-[26px] font-semibold leading-tight tracking-tight" style={{ fontFamily: 'var(--font-display)', fontVariationSettings: "'opsz' 36" }}>
              Drop a PDF to begin
            </h2>
            <p className="mt-2 text-[14px] leading-relaxed text-slate-500 dark:text-slate-400">
              Drag a file from your computer, paste from the clipboard, or use the upload button in the top bar.
            </p>
          </div>

          <div className="mt-7 flex items-center gap-2 w-72">
            <div className="h-px flex-1 bg-[var(--border-hairline)]" />
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              Or
            </div>
            <div className="h-px flex-1 bg-[var(--border-hairline)]" />
          </div>

          <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-state-success px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-state-success">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Your documents never leave this machine
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[520px] px-6">
        <div className="w-full max-w-[720px]">
          {/* Skeleton pages while PDF is loading */}
          {[1, 2].map((n) => (
            <div key={n} className="mb-8 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="h-8 bg-slate-100 flex items-center px-4">
                <div className="h-3 w-16 bg-slate-200 rounded" />
              </div>
              <div className="p-8 space-y-3 animate-pulse">
                <div className="h-4 bg-slate-100 rounded w-3/4" />
                <div className="h-4 bg-slate-100 rounded w-1/2" />
                <div className="h-4 bg-slate-100 rounded w-5/6" />
                <div className="pt-8">
                  <div className="h-3 bg-slate-100 rounded w-1/3" />
                </div>
              </div>
            </div>
          ))}
          <div className="text-center text-sm text-slate-500 mt-2">Loading document…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-state-danger">
        <div className="font-semibold">Could not load PDF</div>
        <div className="text-sm mt-1 text-state-danger">{error}</div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container">
      <div ref={pageColumnRef} className="w-full max-w-[820px] mx-auto">
        {/* Visual compare banner - prominent, always visible at top of viewer content when active.
            Click to exit. Keeps main fields fully interactive underneath the ghost layer. */}
        {/* ENHANCED: now includes lightweight diff summary for instant visual confidence (what changed vs the snapshot ghost) */}
        {compareFields && compareFields.length > 0 && (
          <div
            onClick={() => onExitCompare?.()}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onExitCompare) { e.preventDefault(); onExitCompare(); } }}
            className="mb-4 rounded-xl border border-accent-muted bg-accent-soft-strong px-4 py-2.5 text-sm shadow-sm flex items-start justify-between cursor-pointer hover:bg-accent-muted dark:bg-accent-soft-strong dark:border-accent dark:text-accent-muted dark:hover:bg-accent transition-colors focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent"
            role="button"
            tabIndex={0}
            aria-label={`Comparing to snapshot ${compareLabel || 'snapshot'}. ${diffSummary ? diffSummary + '. ' : ''}Press Enter or click to exit compare mode.`}
          >
            <div>
              <div className="flex items-center gap-2">
                <span className="text-accent">◉</span>
                <span>
                  <span className="font-semibold text-accent dark:text-accent-muted">Comparing to:</span>{' '}
                  <span className="font-medium">{compareLabel || 'snapshot'}</span>
                </span>
              </div>
              {/* Tiny high-impact diff summary line (e.g. "+2 added • -1 removed • 3 changed • 5 same") */}
              {diffSummary && (
                <div className="mt-0.5 pl-6 text-[10px] font-mono text-accent dark:text-accent-muted tracking-tight">
                  Diff: <span className="font-semibold">{diffSummary}</span>
                </div>
              )}
            </div>
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-accent-muted dark:bg-accent-soft-strong text-accent dark:text-accent-muted mt-0.5">
              Click to exit compare
            </span>
          </div>
        )}

        {pageIndices.map((idx) => {
          const pageSize = pageSizes[idx] || { width: 612, height: 792 };
          // Pass only this page's fields. The per-page bucket keeps a stable array
          // identity for every page whose fields did NOT change, so React.memo on
          // PdfPage can bail out instead of re-rendering (and re-filtering the full
          // field list) on every single-field edit elsewhere in the document.
          const pageFields = fieldsByPage.get(idx) ?? EMPTY_FIELDS;
          return (
            <PdfPage
              key={idx}
              pdfDoc={pdfDoc!}
              pageIndex={idx}
              scale={scale}
              fields={pageFields}
              selectedFieldId={selectedFieldId}
              isPlacing={isPlacing}
              placementType={isPlacing ? currentTool : null}
              onPlaceField={onPlaceField}
              onFieldUpdate={onFieldUpdate}
              onFieldSelect={onFieldSelect}
              onFieldDelete={onFieldDelete}
              inlineEditingFieldId={inlineEditingFieldId}
              onStartInlineEdit={onStartInlineEdit}
              onCommitInlineEdit={onCommitInlineEdit}
              onCancelInlineEdit={onCancelInlineEdit}
              inlineEditDismissRef={inlineEditDismissRef}
              pageWidth={pageSize.width}
              pageHeight={pageSize.height}
              // Pass loading state so individual pages can show subtle loading if needed
              isLoadingSizes={!hasAccurateSizes}
              // Allow active pages to contribute to the thumbnail navigator (high-impact reuse)
              onThumbnailUpdate={handleThumbnailUpdate}
              onPageViewportMeasured={handlePageViewportMeasured}
              // High-impact compare mode: hide fields to see the original PDF
              showPlacedFields={showPlacedFields}
              // Snapshot compare fields (rendered as ghost layer)
              compareFields={compareFields}
              // Pre-computed ghost diff status (Map<id, status>) — avoids each PdfPage
              // doing an O(F) filter+find for every ghost on every render.
              ghostStatusById={ghostStatusById}
              // Dynamic opacity for ghosts + label (for potential per-page use; banner lives in viewer)
              compareOpacity={compareOpacity}
              // Global pan mode - disable field interactions during pan
              panMode={effectivePanMode}
              isCurrentPage={idx === activePageIndex}
              // Pass ink capture callback (threaded to PdfPage for 'ink' tool strokes)
              onPlaceInk={onPlaceInk}
            />
          );
        })}

      </div>

      {/* Zoom controls — fixed above the pages filmstrip so they stay visible while scrolling */}
      {pageCount > 0 && (
        <div
          className="editor-viewer-zoom flex items-center gap-1 rounded-2xl border border-[var(--border-hairline)] bg-[var(--bg-panel)]/95 px-2 py-1 shadow-lg backdrop-blur"
          role="toolbar"
          aria-label="Document zoom, compare, and pan controls"
        >
          <button
            onClick={() => zoomBy(-0.25)}
            disabled={scale <= 0.5}
            className="px-2 py-1 text-lg leading-none hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-40"
            title="Zoom out (Ctrl/Cmd + scroll)"
            aria-label="Zoom out"
          >
            −
          </button>
          <div className="min-w-[52px] text-center font-mono text-sm tabular-nums text-slate-600 dark:text-slate-300">
            {Math.round(scale * 100)}%
          </div>
          <button
            onClick={() => zoomBy(0.25)}
            disabled={scale >= 4}
            className="px-2 py-1 text-lg leading-none hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-40"
            title="Zoom in (Ctrl/Cmd + scroll)"
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            onClick={fitToWidth}
            className="ml-1 px-2 py-0.5 text-xs rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600"
            aria-label="Fit page width to viewer"
            title="Fit page width to viewer"
          >
            Fit width
          </button>
          <button
            onClick={fitToPage}
            className="ml-1 px-2 py-0.5 text-xs rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600"
            aria-label="Fit whole page in viewer"
            title="Fit whole page in viewer"
          >
            Fit page
          </button>
          <button
            onClick={fitToHeight}
            className="ml-1 px-2 py-0.5 text-xs rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600"
            aria-label="Fit page height to viewer"
            title="Fit page height to viewer"
          >
            Fit height
          </button>

          {/* Contextual actions for the selected field (duplicate to all pages / date quick-fill) */}
          {(canCopySelectedToAllPages || selectedDateField) && (
            <div className="ml-2 pl-2 border-l border-slate-200 dark:border-slate-600 flex items-center gap-1">
              {canCopySelectedToAllPages && selectedFieldObj && (
                <button
                  onClick={() => onDuplicateToAllPages?.(selectedFieldObj.id)}
                  className="px-2 py-1 text-xs font-medium rounded-lg bg-accent text-white hover:bg-accent transition-colors"
                  title="Place a copy of this field at the same position on every page (Ctrl+Shift+D)"
                  aria-label="Copy selected field to all pages"
                >
                  All pages
                </button>
              )}
              {selectedDateField && (
                <button
                  onClick={() =>
                    onFieldUpdate(selectedDateField.id, {
                      value: formatTodayLike(selectedDateField.value),
                    })
                  }
                  className="px-2 py-1 text-xs font-medium rounded-lg bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                  title="Fill this date field with today's date (matches its current format)"
                  aria-label="Fill selected date field with today's date"
                >
                  Today
                </button>
              )}
            </div>
          )}

          {/* High-impact Compare / Show Original toggle */}
          <div className="ml-2 pl-2 border-l border-slate-200 dark:border-slate-600 flex items-center">
            <button
              onClick={() => setShowPlacedFields(!showPlacedFields)}
              className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                showPlacedFields 
                  ? 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600' 
                  : 'bg-state-success text-white hover:bg-state-success'
              }`}
              title={showPlacedFields ? "Hide placed fields to see the original PDF" : "Show placed fields again"}
              aria-label={showPlacedFields ? "Hide placed fields to see the original PDF" : "Show placed fields again"}
              aria-pressed={!showPlacedFields}
            >
              {showPlacedFields ? "Show Original" : "Show Fields"}
            </button>
          </div>

          {/* Ghost opacity slider - only visible during snapshot compare (0.2–0.8 range) */}
          {compareFields && compareFields.length > 0 && (
            <div className="ml-2 pl-2 border-l border-slate-200 dark:border-slate-600 flex items-center gap-1.5">
              <span className="text-[10px] text-slate-500 dark:text-slate-400 select-none" title="Adjust visibility of the snapshot ghost overlay">Ghost</span>
              <input
                type="range"
                min={0.2}
                max={0.8}
                step={0.05}
                value={compareOpacity}
                onChange={(e) => setCompareOpacity(parseFloat(e.target.value))}
                className="w-16 accent-accent cursor-pointer"
                aria-label={`Snapshot ghost overlay opacity, ${Math.round(compareOpacity * 100)} percent`}
                title={`Snapshot ghost opacity: ${Math.round(compareOpacity * 100)}%`}
              />
              <span className="font-mono text-[10px] tabular-nums w-7 text-right text-slate-600 dark:text-slate-300">
                {Math.round(compareOpacity * 100)}%
              </span>
            </div>
          )}

          {/* Pan hand tool toggle (secondary - primary is the global toolbar pan control) */}
          <button
            onClick={() => setEffectivePan(!effectivePanMode)}
            className={`ml-1 px-2 py-1 text-sm rounded-lg transition-colors ${
              effectivePanMode 
                ? 'bg-accent text-white' 
                : 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600'
            }`}
            title={effectivePanMode ? "Exit pan mode (hand tool)" : "Activate pan mode (hand tool) - drag to scroll the document"}
            aria-label={effectivePanMode ? "Exit pan mode (hand tool)" : "Activate pan mode (hand tool) - drag to scroll the document"}
            aria-pressed={effectivePanMode}
          >
            ✋
          </button>
        </div>
      )}

    </div>
  );
};

// Memoize so incidental SigningWorkspace re-renders (zoom, pan toggle, status
// messages, inline-edit start, selection changes without a `fields` change) skip
// the viewer entirely — its body (including the fieldsByPage useMemo) and the
// reconciliation of N PdfPage children only run again when a prop actually
// changes. During an active drag `fields` gets a fresh array each pointermove,
// so the viewer still re-renders as before. NOTE: full bailout also requires the
// SigningWorkspace call site to pass stable (useCallback-wrapped) handlers; any
// inline-arrow prop there will still defeat memo until those are stabilized.
/**
 * [A10] PdfViewer previously re-rendered on EVERY SigningWorkspace state change
 * (zoom, pan, status message, liveThumbnails, activePageIndex) because the parent
 * passes ~16 inline-arrow handlers whose identity changes each render, so a default
 * shallow memo never bailed. The handlers are only ever invoked, and the state they
 * close over (`fields`, `inlineEditingFieldId`, ...) is itself a compared data prop
 * below — so it is safe to ignore handler identity and re-render only when a prop
 * that actually drives the rendered output changes.
 */
const arePropsEqual = (prev: PdfViewerProps, next: PdfViewerProps): boolean => {
  return (
    prev.file === next.file &&
    prev.fileId === next.fileId &&
    prev.fields === next.fields &&
    prev.selectedFieldId === next.selectedFieldId &&
    prev.currentTool === next.currentTool &&
    prev.scale === next.scale &&
    prev.compareFields === next.compareFields &&
    prev.compareLabel === next.compareLabel &&
    prev.panMode === next.panMode &&
    prev.inlineEditingFieldId === next.inlineEditingFieldId &&
    prev.activePageIndex === next.activePageIndex &&
    prev.sharedPdf === next.sharedPdf &&
    prev.documentName === next.documentName
  );
};

export const PdfViewer = memo(PdfViewerComponent, arePropsEqual);
